import { Link } from "@inertiajs/react";
import { ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DataTable, Th, Td, rowClass } from "@/components/shared/DataTable";
import StatusBadge from "@/components/shared/StatusBadge";
import { formatRoom } from "@/lib/classroom";
import { formatMoney, formatDateTime } from "@/lib/format";

// Why a payment didn't go through, in words (PaymentAttempt::ERROR_CATEGORIES).
const FAILURE_REASONS = {
  decline: "Card declined",
  config: "Payment setup problem",
  transient: "Temporary problem",
  unknown: "Didn't go through",
};

/**
 * The donations table from the school's Donations page. Pure
 * presentational — pass `donations` as props. The page also handles
 * the summary line + pagination above/below; the help centre embeds
 * just this table.
 *
 * Admins see the donor's real name; a donation marked anonymous shows
 * as "Anonymous" on public pages, which the tag says.
 */
export default function TransactionsTable({ donations }) {
  return (
    <DataTable>
      <thead>
        <tr>
          <Th>Date</Th>
          <Th>Donor</Th>
          <Th>Student</Th>
          <Th>Classroom</Th>
          <Th align="right">Donation</Th>
          <Th align="right">
            <span title="What the donor's card was charged, including any processing fee and tip they chose to cover">Charged</span>
          </Th>
          <Th align="center">Status</Th>
          <Th srOnly>Details</Th>
        </tr>
      </thead>
      <tbody>
        {donations.map((d) => (
          <tr key={d.id} className={rowClass}>
            <Td className="whitespace-nowrap text-muted-foreground">{formatDateTime(d.paid_at || d.created_at)}</Td>
            <Td>
              <div className="flex flex-wrap items-center gap-1.5 font-medium text-foreground">
                {d.donor_name}
                {d.anonymous && <Badge variant="secondary" className="text-[11px]">Anonymous to the public</Badge>}
              </div>
              {d.donor_email && <div className="text-xs text-muted-foreground">{d.donor_email}</div>}
            </Td>
            <Td className="text-foreground">
              {d.student_path ? (
                <Link href={d.student_path} className="font-medium hover:text-primary">{d.student_name}</Link>
              ) : (
                d.student_name
              )}
            </Td>
            <Td className="text-muted-foreground">{formatRoom(d.classroom_name)}</Td>
            <Td align="right" className="text-foreground">{formatMoney(d.amount_cents, { cents: true })}</Td>
            <Td align="right" className="text-muted-foreground">{formatMoney(d.total_charged_cents, { cents: true })}</Td>
            <Td align="center">
              <StatusBadge status={d.status} />
            </Td>
            <Td align="center" className="whitespace-nowrap">
              {d.provider_dashboard_url ? (
                <a href={d.provider_dashboard_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                  View in {d.provider === "square" ? "Square" : "provider"}
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </a>
              ) : d.status === "failed" ? (
                <span className="text-xs text-destructive" title={d.error_message || ""}>
                  {FAILURE_REASONS[d.error_category] || FAILURE_REASONS.unknown}
                </span>
              ) : null}
            </Td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  );
}
